/**
 * Analysis pipeline API service.
 *
 * Communicates with the /analysis/ endpoints to query status,
 * start runs, and retry failed photos.
 */

import apiClient from '@/api/client';
import { ANALYSIS, EVENTS } from '@/api/endpoints';
import type { AnalysisStatus, StartAnalysisRequest } from '@/api/types/analysis.types';

export const analysisService = {
  /**
   * Get the current analysis status for an event.
   */
  async getAnalysisStatus(eventId: string): Promise<AnalysisStatus> {
    const { data } = await apiClient.get<AnalysisStatus>(ANALYSIS.STATUS, {
      params: { event_id: eventId },
    });
    return data;
  },

  /**
   * Start (or resume) the analysis pipeline for an event.
   */
  async startAnalysis(eventId: string, stages?: string[], forceRerun = false): Promise<void> {
    const payload: StartAnalysisRequest = {
      event_id: eventId,
      stages: stages as StartAnalysisRequest['stages'],
      force_rerun: forceRerun,
    };
    await apiClient.post(ANALYSIS.START, payload);
  },

  /**
   * Retry failed photos for an event.
   */
  async retryFailed(eventId: string): Promise<void> {
    await apiClient.post(`${ANALYSIS.STATUS}retry/`, { event_id: eventId });
  },

  /**
   * Trigger automatic phase detection for an event.
   */
  async autoDetectPhases(eventId: string) {
    const { data } = await apiClient.post(
      `${EVENTS.PHASES(eventId)}auto-detect/`,
    );
    return data;
  },
};
